import { Chart } from 'components/Chart/Chart'
import { createPath } from 'history'
import qs from 'qs'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { COLORS } from 'modules/Statistics/colors'
import StatisticsLayout from 'modules/Statistics/subcomponents/Layout'
import Breadcrumbs from 'components/Breadcrumbs'
import { ChartLegend } from 'modules/Statistics/subcomponents/Layout/ChartLegend'
import TableComponent from 'modules/Statistics/Account/subcomponents/TableComponent'
import NETWORK_PREFIX_MAP from 'modules/Statistics/networkPrefixMap'
import styles from 'modules/Statistics/statistics.module.scss'
import useChartMeasures from 'modules/Statistics/Account/useChartMeasures'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import FilterPanel from 'components/Filters/FilterPanel'
import config from 'modules/Statistics/Account/filters'
import omit from 'lodash/omit'
import isEqual from 'lodash/isEqual'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import useChangeChartType from 'modules/Statistics/hooks/useChangeChartType'
import {
  ACCOUNT_FETCH_CHART,
  ACCOUNT_SET_FILTER,
  ACCOUNT_SETUP_CHART_CONFIG,
  GET_ACCOUNT_LOGIN,
  TOGGLE_METRIC_HIDDEN,
} from 'modules/Statistics/types'

const StatisticsAccount = () => {
  const pathGen = useGeneratePathI18n()
  const { t } = useTranslation(['statistics'])
  const dispatch = useDispatch()

  const [cfg, setCfg] = useLocalStorage('statisticsConfig', {
    showStatisticsChart: true,
  })

  const [chartVisible, setChartVisible] = useState(cfg.showStatisticsChart)

  const { network, externalId } = useParams()

  const { chart } = useSelector((state) => state['statistics/account'])

  const filterManager = useFiltersManager({
    filters: useSelector((state) => state['statistics/account'].filters),
    config: config(network),
    additionalFilterState: useSelector((state) => state['statistics/filters'].filters),
    setAction: ACCOUNT_SET_FILTER,
    nonClearableFilters: ['date_group_period', 'include_vat'],
  })

  const [filtersState, setAccountFiltersState] = useState(
    omit(filterManager.filters, ['include_vat']),
  )

  useEffect(() => {
    const omittedFilters = omit(filterManager.filters, ['include_vat'])
    !isEqual(filtersState, omittedFilters) && setAccountFiltersState(omittedFilters)
  }, [filterManager.filters])

  const chartConfig = useChartMeasures(network)

  useEffect(() => {
    dispatch({
      type: ACCOUNT_SETUP_CHART_CONFIG,
      payload: chartConfig,
    })
  }, [chartConfig])

  useEffect(() => {
    filterManager.filters &&
      dispatch({
        type: ACCOUNT_FETCH_CHART,
        payload: {
          externalId: externalId,
          network: NETWORK_PREFIX_MAP[network],
        },
      })
  }, [filtersState])

  useEffect(() => {
    if (externalId) {
      const net = NETWORK_PREFIX_MAP[network]

      if (net !== 'yd') {
        return
      }
      dispatch({
        type: GET_ACCOUNT_LOGIN,
        payload: { externalId, network: net },
      })
    }
  }, [externalId])

  const changeChartType = useChangeChartType(chart)

  useEffect(() => {
    setCfg({ showStatisticsChart: chartVisible })
  }, [chartVisible])

  return (
    <StatisticsLayout
      chartVisible={chartVisible}
      BreadcrumbsComponent={
        <Breadcrumbs
          links={[
            {
              title: t('breadcrumbs.back_to_accounts_list'),
              to: createPath({
                pathname: pathGen(ROUTES.STATISTICS),
                search: qs.stringify(filterManager.params, {
                  arrayFormat: 'brackets',
                }),
              }),
            },
          ]}
        />
      }
      FilterPanel={
        filterManager.filters && (
          <FilterPanel
            filterManager={filterManager}
            className={styles['filter-panel']}
          />
        )
      }
      ChartComponent={
        <Chart
          chartHeaderClassName={styles['chart-header_tab-legend']}
          colors={COLORS}
          className={styles.chart}
          chartType={chart.chartType}
          items={chart.data}
          metrics={chart.metrics}
          loading={chart.loading}
          date_group_period={{
            value: filterManager.filters?.date_group_period ?? 'day',
            options: filterManager.configuration.date_group_period.options,
          }}
          include_vat={filterManager.filters?.include_vat}
          summary={chart.summary}
          measure={chart.measure}
          changeChartPeriod={(value) =>
            filterManager.updateFilter({
              fieldName: 'date_group_period',
              value: value,
            })
          }
          changeChartType={changeChartType}
          chartLegend={
            <ChartLegend
              include_vat={filterManager.filters?.include_vat}
              measure={chart.measure}
              metrics={chart.metrics}
              summary={chart.summary}
              handleClick={(id) => {
                dispatch({
                  type: TOGGLE_METRIC_HIDDEN,
                  payload: { id },
                })
              }}
            />
          }
        />
      }
      TableComponent={
        filterManager.filters && (
          <TableComponent
            chartVisible={chartVisible}
            setChartVisible={setChartVisible}
            filters={filterManager.filters}
          />
        )
      }
    />
  )
}

StatisticsAccount.propTypes = {}

export default StatisticsAccount
